import React, { useState, useEffect } from 'react';
import api from '../config/api';

function CompletionAgentExecution({ fieldValues, onFieldUpdate, inventoryTypeId }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [multipleResults, setMultipleResults] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);

  useEffect(() => {
    if (inventoryTypeId) {
      fetchActiveAgents();
    }
  }, [inventoryTypeId]);

  const fetchActiveAgents = async () => {
    try {
      const response = await api.get('/completion-agents');
      // Filter only active agents for this inventory type
      setAgents(response.data.filter(agent => 
        agent.is_active && 
        agent.inventory_type_id === inventoryTypeId
      ));
    } catch (error) {
      console.error('Error fetching completion agents:', error);
    }
  };

  const executeAgent = async (agent) => {
    // Get the value from the specific field this agent is configured to use
    const agentSearchValue = fieldValues[agent.search_field];
    
    if (!agentSearchValue || !agentSearchValue.toString().trim()) {
      setError(`Please fill in the "${agent.search_field}" field first`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(`/completion-agents/${agent.id}/execute`, {
        search_value: agentSearchValue.toString().trim()
      });

      if (response.data.success) {
        if (response.data.multiple_results && response.data.multiple_results.length > 0) {
          if (response.data.multiple_results.length === 1) {
            // Single result in multiple results array - apply directly
            applyResultData(response.data.multiple_results[0], agent.name);
          } else {
            // Multiple results found - show selection modal
            setMultipleResults({
              agent: agent,
              results: response.data.multiple_results,
              searchValue: agentSearchValue.toString().trim()
            });
            setShowResultsModal(true);
          }
        } else if (response.data.data) {
          // Single result - apply directly
          applyResultData(response.data.data, agent.name);
        } else {
          setError('No data returned from the completion agent');
        }
      } else {
        setError(response.data.error || 'No data returned from the completion agent');
      }
    } catch (error) {
      console.error('Error executing completion agent:', error);
      setError(`Failed to execute ${agent.name}: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const applyResultData = (data, agentName) => {
    // Debug logging
    console.log('Applying result data:', data);
    console.log('Available form fields:', Object.keys(fieldValues));
    
    // Apply the mapped data to the form fields (excluding internal fields)
    Object.entries(data).forEach(([fieldName, value]) => {
      if (fieldName !== '_display_name' && value !== null && value !== undefined) {
        // Check if exact field name exists, if not try case-insensitive match
        let targetFieldName = fieldName;
        
        if (!(fieldName in fieldValues)) {
          // Try to find case-insensitive match
          const availableFields = Object.keys(fieldValues);
          const matchingField = availableFields.find(f => 
            f.toLowerCase() === fieldName.toLowerCase()
          );
          
          if (matchingField) {
            targetFieldName = matchingField;
            console.log(`Field name case mismatch: using '${targetFieldName}' instead of '${fieldName}'`);
          } else {
            console.warn(`Field '${fieldName}' not found in form. Available fields:`, availableFields);
            return; // Skip this field
          }
        }
        
        console.log(`Updating field ${targetFieldName} with value:`, value);
        onFieldUpdate(targetFieldName, value);
      }
    });

    // Show success message
    const filledFields = Object.keys(data).filter(key => key !== '_display_name').length;
    alert(`✅ Successfully filled ${filledFields} field(s) using ${agentName}`);
  };

  const handleResultSelection = (selectedResult) => {
    applyResultData(selectedResult, multipleResults.agent.name);
    setShowResultsModal(false);
    setMultipleResults(null);
  };

  const handleResultsModalClose = () => {
    setShowResultsModal(false);
    setMultipleResults(null);
  };

  if (agents.length === 0) {
    return null; // Don't show anything if no active agents
  }

  return (
    <div style={{ 
      marginBottom: '1rem', 
      padding: '1rem', 
      backgroundColor: '#f8f9fa', 
      borderRadius: '4px',
      border: '1px solid #e9ecef'
    }}>
      <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>🤖 Auto-Complete Fields</h4>
      
      {error && (
        <div style={{ 
          color: '#e74c3c', 
          backgroundColor: '#fdf2f2', 
          padding: '0.5rem', 
          borderRadius: '4px',
          marginBottom: '1rem',
          fontSize: '0.9rem'
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {agents.map((agent) => {
          const agentSearchValue = fieldValues[agent.search_field];
          const hasSearchValue = agentSearchValue && agentSearchValue.toString().trim();
          return (
            <button
              key={agent.id}
              type="button"
              className="btn btn-secondary"
              onClick={() => executeAgent(agent)}
              disabled={loading || !hasSearchValue}
              style={{ 
                fontSize: '0.8rem', 
                padding: '0.5rem 1rem',
                opacity: !hasSearchValue ? 0.6 : 1
              }}
              title={!hasSearchValue ? `Fill in "${agent.search_field}" field first` : `Search using ${agent.name}`}
            >
              {loading ? '⏳' : '🔍'} {agent.name}
            </button>
          );
        })}
      </div>
      
      <p style={{ 
        margin: '0.5rem 0 0 0', 
        fontSize: '0.8rem', 
        color: '#666',
        fontStyle: 'italic'
      }}>
        Fill in the search field and click a completion agent to automatically populate fields
      </p>
      
      {showResultsModal && multipleResults && (
        <ResultSelectionModal
          agent={multipleResults.agent}
          results={multipleResults.results}
          searchValue={multipleResults.searchValue}
          onSelect={handleResultSelection}
          onClose={handleResultsModalClose}
        />
      )}
    </div>
  );
}

function ResultSelectionModal({ agent, results, searchValue, onSelect, onClose }) {
  return (
    <div className="modal" style={{ zIndex: 10000 }}>
      <div className="modal-content" style={{ maxWidth: '800px', width: '90vw' }}>
        <h2>Multiple Results Found</h2>
        <p style={{ marginBottom: '1rem', color: '#666' }}>
          {agent.name} found {results.length} results for "{searchValue}". Please select the correct one:
        </p>
        
        <div style={{ 
          maxHeight: '500px', 
          overflowY: 'auto',
          border: '1px solid #ddd',
          borderRadius: '4px'
        }}>
          {results.map((result, index) => (
            <div key={index} style={{
              border: '1px solid #eee',
              margin: '0.5rem',
              padding: '1rem',
              borderRadius: '4px',
              backgroundColor: '#fafafa',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onClick={() => onSelect(result)}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#fafafa'}
            >
              <h4 style={{ margin: '0 0 0.5rem 0' }}>
                {result._display_name || `Option ${index + 1}`}
              </h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '0.5rem' 
              }}>
                {Object.entries(result)
                  .filter(([fieldName]) => fieldName !== '_display_name') // Don't show internal display field
                  .map(([fieldName, value]) => (
                    <div key={fieldName} style={{ fontSize: '0.9rem' }}>
                      <strong>{fieldName}:</strong> {
                        Array.isArray(value) ? value.join(', ') : 
                        (value?.toString() || 'N/A')
                      }
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '1rem', 
          marginTop: '1rem' 
        }}>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default CompletionAgentExecution;